import { Button, Card, CardContent, PageHeader, StatusTimeline, TextareaField } from "@jod/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { hasPermission } from "../../features/ops/permissions.ts";
import { useCreateIncident, useReport, useReportAction } from "../../features/ops/queries.ts";
import { getStaffPrincipal } from "../../lib/auth-session.ts";

export const Route = createFileRoute("/_app/moderation/$reportId")({
  component: ReportDetailScreen,
});

function ReportDetailScreen() {
  const principal = getStaffPrincipal();
  const canAction = hasPermission(principal, "moderation:action");
  const navigate = useNavigate();
  const { reportId } = Route.useParams();
  const { data: report } = useReport(reportId);
  const action = useReportAction();
  const createIncident = useCreateIncident();
  const [reason, setReason] = useState(
    "Operational action taken; reporter identity remains hidden.",
  );
  // SAFE-011 / FR-STAFF-081: banning a real person is high-consequence and
  // always requires its own explicit, non-defaulted reason.
  const [banReason, setBanReason] = useState("");
  if (!report) return <PageHeader title="Report detail" subtitle="Loading report…" />;
  return (
    <div className="space-y-6">
      <PageHeader
        title={report.reason}
        subtitle={`${report.targetType} · ${report.status} · ${report.severity}`}
      />
      <div className="grid gap-4 lg:grid-cols-[1fr_24rem]">
        <Card>
          <CardContent className="space-y-4 p-5">
            <p className="rounded-md bg-muted p-3 text-sm">{report.snapshot}</p>
            <p className="text-sm">Target: {report.targetLabel}</p>
            <p className="text-muted-foreground text-sm">
              No reporter identity or raw contact handle is displayed.
            </p>
            <TextareaField
              id="moderationReason"
              label="Action note"
              value={reason}
              onChange={setReason}
            />
            <div className="flex flex-wrap gap-2">
              <Button
                disabled={!canAction}
                onClick={() => action.mutate({ reportId, action: "warn", reason })}
              >
                Warn
              </Button>
              <Button
                disabled={!canAction}
                onClick={() => action.mutate({ reportId, action: "mute_social", reason })}
              >
                Mute social
              </Button>
              <Button
                disabled={!canAction}
                onClick={() => action.mutate({ reportId, action: "disable_social", reason })}
              >
                Disable social / ซ่อนผู้ถูกรายงาน
              </Button>
              <Button
                variant="outline"
                disabled={!canAction}
                onClick={() => action.mutate({ reportId, action: "dismiss", reason })}
              >
                Dismiss
              </Button>
              <Button
                variant="destructive"
                disabled={!canAction}
                onClick={() => action.mutate({ reportId, action: "escalate", reason })}
              >
                Escalate
              </Button>
              <Button
                variant="outline"
                disabled={!canAction}
                onClick={() =>
                  createIncident.mutate(
                    {
                      title: `Incident: ${report.reason}`,
                      severity: report.severity,
                      description: reason,
                      reportId,
                    },
                    { onSuccess: () => navigate({ to: "/incidents" }) },
                  )
                }
              >
                Create incident
              </Button>
            </div>

            <div className="space-y-2 rounded-md border border-destructive/30 bg-destructive/5 p-3">
              <h3 className="font-display font-semibold text-destructive text-sm">
                Ban from venue / แบนจากร้าน (SAFE-011)
              </h3>
              <TextareaField
                id="banReason"
                label="Ban reason (required) / เหตุผล (จำเป็น)"
                value={banReason}
                onChange={setBanReason}
                placeholder="Required — e.g. repeated harassment tonight"
              />
              <Button
                variant="destructive"
                disabled={!canAction || banReason.trim().length === 0}
                title={
                  !canAction
                    ? "Requires moderation:action"
                    : banReason.trim().length === 0
                      ? "A reason is required to ban"
                      : undefined
                }
                onClick={() => action.mutate({ reportId, action: "ban", reason: banReason.trim() })}
              >
                Ban user / แบนผู้ใช้
              </Button>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="space-y-4 p-5">
            <h2 className="font-display font-semibold text-lg">History</h2>
            <StatusTimeline
              items={report.history.map((item) => ({
                id: item.id,
                label: item.action,
                timestamp: item.createdAt,
                description: item.reason,
              }))}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
